

/* ========================================================================== */
/*  FILE: components/home/CTAClient.tsx                                        */
/* ========================================================================== */

"use client";

import React, { useMemo } from "react";
import Link from "next/link";
import {
  ArrowRight,
  Award,
  CheckCircle2,
  Clock,
  Download,
  Heart,
  Shield,
  Sparkles,
  UtensilsCrossed,
  Zap,
} from "lucide-react";

export type CTAClientProps = {
  heading?: string;
  subheading?: string;
  badgeText?: string;
  primaryCtaHref?: string;
  primaryCtaLabel?: string;
  secondaryCtaHref?: string;
  secondaryCtaLabel?: string;
  bullets?: string[];
};

function cn(...classes: Array<string | undefined | null | false>) {
  return classes.filter(Boolean).join(" ");
}

export default function CTAClient(props: CTAClientProps) {
  const heading = props.heading ?? "Join Malawi’s Dining Revolution Today";
  const subheading =
    props.subheading ??
    "Whether you’re looking for amazing food or want to grow your restaurant business, IndeMenu gives you everything you need.";
  const badgeText = props.badgeText ?? "Ready to Transform Your Dining Experience?";
  const primaryCtaHref = props.primaryCtaHref ?? "/download";
  const primaryCtaLabel = props.primaryCtaLabel ?? "Download App";
  const secondaryCtaHref = props.secondaryCtaHref ?? "/dashboards/vendor";
  const secondaryCtaLabel = props.secondaryCtaLabel ?? "For Restaurants";

  const bullets = useMemo(
    () =>
      props.bullets ?? [
        "Fast discovery & menu browsing",
        "Order ahead, scan QR, or dine-in",
        "Secure payments & receipts",
        "Built for Malawi — optimized performance",
      ],
    [props.bullets]
  );

  return (
    <section
      className="relative py-20 sm:py-24 overflow-hidden bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600"
      aria-label="Call to action"
    >
      {/* Subtle pattern */}
      <div className="absolute inset-0 opacity-15">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 25% 25%, rgba(255,255,255,0.35) 1px, transparent 1px)",
            backgroundSize: "52px 52px",
          }}
        />
      </div>

      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(255,255,255,0.22),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_40%,rgba(255,255,255,0.18),transparent_60%)]" />

      <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 text-center">
        <div className="mx-auto w-fit inline-flex items-center gap-2 mb-8 rounded-full bg-white/20 backdrop-blur-xl px-6 py-3 border border-white/30 shadow-lg">
          <Award className="h-5 w-5 text-white" />
          <span className="text-sm font-semibold text-white">{badgeText}</span>
          <span className="ml-2 hidden sm:inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 text-[11px] text-white/90">
            <Sparkles className="h-3.5 w-3.5" />
            New
          </span>
        </div>

        <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight text-balance">
          {heading}
        </h2>

        <p className="mt-6 text-lg sm:text-xl text-white/90 leading-relaxed max-w-3xl mx-auto">
          {subheading}
        </p>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
          {bullets.slice(0, 4).map((b, idx) => (
            <div
              key={`${b}-${idx}`}
              className="flex items-start gap-3 rounded-2xl bg-white/15 border border-white/25 backdrop-blur-xl px-5 py-4 text-left"
            >
              <CheckCircle2 className="h-5 w-5 text-white mt-0.5" />
              <div className="text-white font-semibold">{b}</div>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-col sm:flex-row gap-5 justify-center items-center">
          <Link
            href={primaryCtaHref}
            className={cn(
              "group inline-flex items-center justify-center gap-4 rounded-2xl px-10 py-5",
              "bg-white text-amber-700 font-extrabold shadow-2xl min-w-[240px]",
              "hover:bg-neutral-100 transition-transform duration-200 hover:scale-[1.01] active:scale-[0.98]"
            )}
          >
            <Download className="h-6 w-6" />
            <span>{primaryCtaLabel}</span>
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>

          <Link
            href={secondaryCtaHref}
            className={cn(
              "group inline-flex items-center justify-center gap-4 rounded-2xl px-10 py-5 min-w-[240px]",
              "bg-transparent text-white font-extrabold border-2 border-white/70",
              "hover:bg-white/10 transition-transform duration-200 hover:scale-[1.01] active:scale-[0.98]"
            )}
          >
            <UtensilsCrossed className="h-6 w-6" />
            <span>{secondaryCtaLabel}</span>
          </Link>
        </div>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-5">
          <div className="rounded-2xl bg-white/15 border border-white/25 backdrop-blur-xl p-5">
            <div className="mx-auto h-12 w-12 rounded-full bg-white/20 flex items-center justify-center mb-3">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div className="font-extrabold text-white">Secure</div>
            <div className="text-sm text-white/85">Bank-level security</div>
          </div>

          <div className="rounded-2xl bg-white/15 border border-white/25 backdrop-blur-xl p-5">
            <div className="mx-auto h-12 w-12 rounded-full bg-white/20 flex items-center justify-center mb-3">
              <Zap className="h-6 w-6 text-white" />
            </div>
            <div className="font-extrabold text-white">Fast</div>
            <div className="text-sm text-white/85">Lightning quick</div>
          </div>

          <div className="rounded-2xl bg-white/15 border border-white/25 backdrop-blur-xl p-5">
            <div className="mx-auto h-12 w-12 rounded-full bg-white/20 flex items-center justify-center mb-3">
              <Clock className="h-6 w-6 text-white" />
            </div>
            <div className="font-extrabold text-white">24/7</div>
            <div className="text-sm text-white/85">Always available</div>
          </div>

          <div className="rounded-2xl bg-white/15 border border-white/25 backdrop-blur-xl p-5">
            <div className="mx-auto h-12 w-12 rounded-full bg-white/20 flex items-center justify-center mb-3">
              <Heart className="h-6 w-6 text-white" />
            </div>
            <div className="font-extrabold text-white">Free</div>
            <div className="text-sm text-white/85">No hidden fees</div>
          </div>
        </div>

        <div className="mt-10 inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/25 backdrop-blur-xl px-6 py-3 text-xs text-white/90">
          <Sparkles className="h-4 w-4" />
          Built with performance-first UI patterns and accessibility in mind.
        </div>
      </div>
    </section>
  );
}
